import { instance as getApi, apiUrl, appUrl } from '@/helpers/api.js';
import { useBrowserSession } from '@/helpers/browser_session';
import { getI18n } from './i18n';
import { useAuthUser } from './globals';

const browserSession = useBrowserSession('eco2stat');

/**
 * Stores the CSRF token in the browser's localStorage
 * for further use during the session.
 *
 * This is needed to support multiple browser windows.
 *
 * @param {String} token
 */
export function storeCsrfToken(token: string) {
    browserSession.setSessionValue('csrfToken', token);
}

/**
 * Retrieves the csrf token from the browser's
 * local storage
 */
export function getCsrfToken() {
    return browserSession.getSessionValue('csrfToken') ?? '';
}

export function storeSessionToken(token: string) {
    browserSession.setSessionValue('sessionToken', token);
}

/**
 * Retrieves the Session token from the browser's
 * local storage
 */
export function getSessionToken() {
    return browserSession.getSessionValue('sessionToken') ?? '';
}

export enum AuthLevel {
    USERNAME_PASSWORD_AUTH = 'username_pw',
    PASSWORD_CHANGE_REQUESTED = 'pw_change',
    AUTH_COMPLETE = 'complete',
}

type TotpResponse = {
    secret: string;
    qr: string;
};

export type LoginResponse = {
    user: any;
    redirectUrl: string;
    authLevel: AuthLevel;
    totpComplete: boolean;
};

export async function login(username: string, password: string): Promise<LoginResponse> {
    try {
        const response = await getApi().post('/auth/login', {
            username: username,
            password: password,
        });
        if (response.data.user) {
            let redirectUrl = appUrl('/');
            const split = window.location.href.split('?redirect=');
            if (split.length === 2) {
                redirectUrl = split[1];
            }
            // read session ID for futher requests
            storeSessionToken(response.data?.sessionId || null);
            storeCsrfToken(response.data?.csrfToken || null);
            return { user: response.data.user, redirectUrl: redirectUrl, authLevel: response.data.authLevel, totpComplete: response.data.totpComplete };
        } else {
            throw new Error(getI18n().t('denied_wrong_login'));
        }
    } catch (e) {
        throw new Error(getI18n().t('denied_wrong_login'));
    }
}

export async function generateTotpKey(): Promise<TotpResponse> {
    try {
        const response = await getApi().get('/auth/generateTotpKey')
        if (response.data.secret && response.data.qr) {
            return { secret: response.data.secret, qr: response.data.qr }
        } else {
            throw new Error(getI18n().t('denied_wrong_login'));
        }
    } catch (e) {
        throw new Error(getI18n().t('denied_wrong_login'));
    }
}

export async function resetTotpKey(userId: number): Promise<void> {
    try {
        await getApi().post('/auth/resetTotpKey', { user_id: userId })
    } catch (e) {
        throw new Error(getI18n().t('denied_wrong_login'));
    }
}

export async function verifyTotpCode(code: string): Promise<boolean> {
    try {
        const response = await getApi().post('/auth/verifyTotpCode', {
            code: code,
        });
        return response.data.verified;
    } catch (e) {
        return false;
    }
}

export async function changePassword(username: string, oldPassword: string, newPassword: string): Promise<LoginResponse> {
    const response = await getApi().post('/auth/changePassword', {
        username: username,
        old_password: oldPassword,
        new_password: newPassword,
    });
    if (response.data.user) {
        let redirectUrl = appUrl('/');
        const split = window.location.href.split('?redirect=');
        if (split.length === 2) {
            redirectUrl = split[1];
        }
        // read session ID for futher requests
        storeSessionToken(response.data?.sessionId || null);
        storeCsrfToken(response.data?.csrfToken || null);
        return { user: response.data.user, redirectUrl: redirectUrl, authLevel: response.data.authLevel, totpComplete: response.data.totpComplete };
    } else {
        throw new Error(getI18n().t('denied_wrong_login'));
    }
}

export async function logout() {
    await getApi().get('/auth/logout');
    storeSessionToken('');
}

export async function verifyUser(password: string) {
    try {
        const response = await getApi().post('/auth/verifyUser', {
            password: password,
        });
        return response.data.verified;
    } catch (e) {
        throw new Error('Hoppla, da ist was schief gelaufen...');
    }
}

export default {
    login,
    logout,
    verifyUser,
    // resetPassword,
};
